import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  orderBy,
  query,
  onSnapshot,
  getDocs,
  getDoc,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

class FirebaseService {
  constructor() {
    this.supabase = createClient(supabaseUrl, supabaseAnonKey);
  }

  // Upload image bytes to Supabase and return public URL
  async uploadImageBytes(bytes, filename) {
    try {
      const filePath = `receipts/${filename}`;
      const { error } = await this.supabase.storage
        .from("receipts")
        .upload(filePath, bytes);

      if (error) throw error;

      const { data } = this.supabase.storage
        .from("receipts")
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error("Image upload failed:", error);
      throw new Error("Image upload failed");
    }
  }

  // Upload image file (alternative method for File objects)
  async uploadImageFile(file, filename) {
    try {
      const filePath = `receipts/${filename}`;
      const { error } = await this.supabase.storage
        .from("receipts")
        .upload(filePath, file);

      if (error) throw error;

      const { data } = this.supabase.storage
        .from("receipts")
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error("Image upload failed:", error);
      throw new Error("Image upload failed");
    }
  }

  // Upload multiple images
  async uploadMultipleImages(files) {
    try {
      const uploadPromises = files.map((file, index) =>
        this.uploadImageFile(file, `property-${Date.now()}-${index}`)
      );

      const imageUrls = await Promise.all(uploadPromises);
      return imageUrls;
    } catch (error) {
      console.error("Multiple image upload failed:", error);
      throw new Error("Multiple image upload failed");
    }
  }

  // Add or update property
  async addOrUpdateProperty(property) {
    try {
      const propertyRef = doc(db, "properties", property.id);

      // Ensure property has required fields
      const propertyData = property.toJson
        ? property.toJson()
        : {
            ...property,
            createdAt: property.createdAt || new Date(),
            updatedAt: new Date(),
            views: property.views+1,
            status: property.status || "pending", // Default status
          };

      await setDoc(propertyRef, propertyData, { merge: true });
    } catch (error) {
      console.error("Error adding/updating property:", error);
      throw error;
    }
  }

  // Get property by ID
  async getPropertyById(propertyId) {
    try {
      const propertyRef = doc(db, "properties", propertyId);
      const propertySnap = await getDoc(propertyRef);

      if (propertySnap.exists()) {
        const data = propertySnap.data();
        return {
          id: propertySnap.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : new Date(data.updatedAt),
          approvedAt: data.approvedAt?.toDate
            ? data.approvedAt.toDate()
            : data.approvedAt
            ? new Date(data.approvedAt)
            : null,
          availableFrom: data.availableFrom?.toDate
            ? data.availableFrom.toDate()
            : data.availableFrom
            ? new Date(data.availableFrom)
            : null,
        };
      } else {
        throw new Error("Property not found");
      }
    } catch (error) {
      console.error("Error getting property:", error);
      throw error;
    }
  }

  // Update property status (approve/reject)
  async updatePropertyStatus(propertyId, status, adminName, reason = "") {
    try {
      const propertyRef = doc(db, "properties", propertyId);
      const updateData = {
        status: status,
        approvedBy: adminName,
        approvedAt: new Date().toISOString(),
        statusReason: reason,
        updatedAt: new Date().toISOString(),
      };

      await setDoc(propertyRef, updateData, { merge: true });
    } catch (error) {
      console.error("Error updating property status:", error);
      throw error;
    }
  }

  // Get all admin properties (with status filter option)
  getAdminListedProperties(callback, statusFilter = null) {
    let propertiesQuery;

    if (statusFilter) {
      propertiesQuery = query(
        collection(db, "properties"),
        where("status", "==", "approved"),
        where("listedBy", "==", "admin"),
        orderBy("createdAt", "desc")
      );
    } else {
      propertiesQuery = query(
        collection(db, "properties"),
        where("status", "==", "approved"),
        where("listedBy", "==", "admin"),
        orderBy("createdAt", "desc")
      );
    }

    return onSnapshot(propertiesQuery, (snapshot) => {
      const properties = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : new Date(data.updatedAt),
          approvedAt: data.approvedAt?.toDate
            ? data.approvedAt.toDate()
            : data.approvedAt
            ? new Date(data.approvedAt)
            : null,
          availableFrom: data.availableFrom?.toDate
            ? data.availableFrom.toDate()
            : data.availableFrom
            ? new Date(data.availableFrom)
            : null,
        };
      });
      callback(properties);
    });
  }

  // Get all limited properties (with status filter option)
  getLimitedProperties(callback, statusFilter = null) {
    let propertiesQuery;

    if (statusFilter) {
      propertiesQuery = query(
        collection(db, "properties"),
        where("status", "==", "approved"),
        where("listedBy", "!=", "admin"),
        orderBy("createdAt", "desc")
      );
    } else {
      propertiesQuery = query(
        collection(db, "properties"),
        where("status", "==", "approved"),
        where("listedBy", "!=", "admin"),
        orderBy("createdAt", "desc")
      );
    }

    return onSnapshot(propertiesQuery, (snapshot) => {
      const properties = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : new Date(data.updatedAt),
          approvedAt: data.approvedAt?.toDate
            ? data.approvedAt.toDate()
            : data.approvedAt
            ? new Date(data.approvedAt)
            : null,
          availableFrom: data.availableFrom?.toDate
            ? data.availableFrom.toDate()
            : data.availableFrom
            ? new Date(data.availableFrom)
            : null,
        };
      });
      callback(properties);
    });
  }

  // Get all properties (with status filter option)
  getProperties(callback, statusFilter = null) {
    let propertiesQuery;

    if (statusFilter) {
      propertiesQuery = query(
        collection(db, "properties"),
        where("status", "==", "approved"),
        orderBy("createdAt", "desc")
      );
    } else {
      propertiesQuery = query(
        collection(db, "properties"),
        where("status", "==", "approved"),
        orderBy("createdAt", "desc")
      );
    }

    return onSnapshot(propertiesQuery, (snapshot) => {
      const properties = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : new Date(data.updatedAt),
          approvedAt: data.approvedAt?.toDate
            ? data.approvedAt.toDate()
            : data.approvedAt
            ? new Date(data.approvedAt)
            : null,
          availableFrom: data.availableFrom?.toDate
            ? data.availableFrom.toDate()
            : data.availableFrom
            ? new Date(data.availableFrom)
            : null,
        };
      });
      callback(properties);
    });
  }

  // Get only approved properties (for public listing)
  getApprovedProperties(callback) {
    return this.getProperties(callback, "approved");
  }

  // Get pending properties (for admin review)
  getPendingProperties(callback) {
    return this.getProperties(callback, "pending");
  }

  // Get featured properties (max 4 approved properties)
  getFeaturedProperties(callback, maxLimit = 4) {
    const featuredQuery = query(
      collection(db, "properties"),
      where("status", "==", "approved"),
      orderBy("createdAt", "desc"),
      limit(maxLimit)
    );

    return onSnapshot(featuredQuery, (snapshot) => {
      const properties = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : new Date(data.updatedAt),
          approvedAt: data.approvedAt?.toDate
            ? data.approvedAt.toDate()
            : data.approvedAt
            ? new Date(data.approvedAt)
            : null,
        };
      });
      callback(properties);
    });
  }

  // Get properties once (non-streaming) with optional status filter
  async getPropertiesOnce(statusFilter = null) {
    try {
      let propertiesQuery;

      if (statusFilter) {
        propertiesQuery = query(
          collection(db, "properties"),
          where("status", "==", statusFilter),
          orderBy("createdAt", "desc")
        );
      } else {
        propertiesQuery = query(
          collection(db, "properties"),
          orderBy("createdAt", "desc")
        );
      }

      const snapshot = await getDocs(propertiesQuery);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : new Date(data.updatedAt),
          approvedAt: data.approvedAt?.toDate
            ? data.approvedAt.toDate()
            : data.approvedAt
            ? new Date(data.approvedAt)
            : null,
          availableFrom: data.availableFrom?.toDate
            ? data.availableFrom.toDate()
            : data.availableFrom
            ? new Date(data.availableFrom)
            : null,
        };
      });
    } catch (error) {
      console.error("Error getting properties:", error);
      throw error;
    }
  }

  // Get approved properties once (non-streaming)
  async getApprovedPropertiesOnce() {
    return this.getPropertiesOnce("approved");
  }

  // Get featured properties once (max 4, non-streaming)
  async getFeaturedPropertiesOnce(maxLimit = 4) {
    try {
      const featuredQuery = query(
        collection(db, "properties"),
        where("status", "==", "approved"),
        orderBy("createdAt", "desc"),
        limit(maxLimit)
      );

      const snapshot = await getDocs(featuredQuery);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : new Date(data.updatedAt),
          approvedAt: data.approvedAt?.toDate
            ? data.approvedAt.toDate()
            : data.approvedAt
            ? new Date(data.approvedAt)
            : null,
        };
      });
    } catch (error) {
      console.error("Error getting featured properties:", error);
      throw error;
    }
  }

  // Delete property
  async deleteProperty(id) {
    try {
      const propertyRef = doc(db, "properties", id);
      await deleteDoc(propertyRef);
    } catch (error) {
      console.error("Error deleting property:", error);
      throw error;
    }
  }

  // Add inquiry
  async addInquiry(inquiry) {
    try {
      const inquiryRef = doc(db, "inquiries", inquiry.id);

      // Ensure inquiry has required fields
      const inquiryData = inquiry.toJson
        ? inquiry.toJson()
        : {
            ...inquiry,
            requestedAt: inquiry.requestedAt || new Date(),
          };

      await setDoc(inquiryRef, inquiryData);
    } catch (error) {
      console.error("Error adding inquiry:", error);
      throw error;
    }
  }

  // Get inquiries (real-time stream)
  getInquiries(callback) {
    const inquiriesQuery = query(
      collection(db, "inquiries"),
      orderBy("requestedAt", "desc")
    );

    return onSnapshot(inquiriesQuery, (snapshot) => {
      const inquiries = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          requestedAt: data.requestedAt?.toDate
            ? data.requestedAt.toDate()
            : new Date(data.requestedAt),
        };
      });
      callback(inquiries);
    });
  }

  // Get inquiries once (non-streaming)
  async getInquiriesOnce() {
    try {
      const inquiriesQuery = query(
        collection(db, "inquiries"),
        orderBy("requestedAt", "desc")
      );

      const snapshot = await getDocs(inquiriesQuery);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          requestedAt: data.requestedAt?.toDate
            ? data.requestedAt.toDate()
            : new Date(data.requestedAt),
        };
      });
    } catch (error) {
      console.error("Error getting inquiries:", error);
      throw error;
    }
  }

  // Add contact us message
  async addContactMessage(contactMessage) {
    try {
      const messageRef = doc(db, "contactUsMessages", contactMessage.id);

      const messageData = contactMessage.toJson
        ? contactMessage.toJson()
        : {
            ...contactMessage,
            createdAt: contactMessage.createdAt || new Date(),
            status: contactMessage.status || "unread",
          };

      await setDoc(messageRef, messageData);
    } catch (error) {
      console.error("Error adding contact message:", error);
      throw error;
    }
  }

  // Get all contact messages (real-time stream)
  getContactMessages(callback, statusFilter = null) {
    let messagesQuery;

    if (statusFilter) {
      messagesQuery = query(
        collection(db, "contactUsMessages"),
        where("status", "==", statusFilter),
        orderBy("createdAt", "desc")
      );
    } else {
      messagesQuery = query(
        collection(db, "contactUsMessages"),
        orderBy("createdAt", "desc")
      );
    }

    return onSnapshot(messagesQuery, (snapshot) => {
      const messages = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
        };
      });
      callback(messages);
    });
  }

  // Get contact messages once (non-streaming)
  async getContactMessagesOnce(statusFilter = null) {
    try {
      let messagesQuery;

      if (statusFilter) {
        messagesQuery = query(
          collection(db, "contactUsMessages"),
          where("status", "==", statusFilter),
          orderBy("createdAt", "desc")
        );
      } else {
        messagesQuery = query(
          collection(db, "contactUsMessages"),
          orderBy("createdAt", "desc")
        );
      }

      const snapshot = await getDocs(messagesQuery);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt),
        };
      });
    } catch (error) {
      console.error("Error getting contact messages:", error);
      throw error;
    }
  }

  // Get unread contact messages
  getUnreadContactMessages(callback) {
    return this.getContactMessages(callback, "unread");
  }

  // Update contact message status
  async updateContactMessageStatus(messageId, status) {
    try {
      const messageRef = doc(db, "contactUsMessages", messageId);
      await setDoc(
        messageRef,
        {
          status: status,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Error updating contact message status:", error);
      throw error;
    }
  }

  // Delete contact message
  async deleteContactMessage(id) {
    try {
      const messageRef = doc(db, "contactUsMessages", id);
      await deleteDoc(messageRef);
    } catch (error) {
      console.error("Error deleting contact message:", error);
      throw error;
    }
  }

  // Get contact messages count by status
  async getContactMessagesCount() {
    try {
      const messagesQuery = query(collection(db, "contactUsMessages"));
      const snapshot = await getDocs(messagesQuery);

      const counts = {
        total: 0,
        unread: 0,
        read: 0,
        replied: 0,
      };

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        counts.total++;
        counts[data.status] = (counts[data.status] || 0) + 1;
      });

      return counts;
    } catch (error) {
      console.error("Error getting contact messages count:", error);
      throw error;
    }
  }
}

export const firebaseService = new FirebaseService();
export default FirebaseService;
